import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import globalStyles from "../../../global";
import FooterSr from "../components/Footersr";
import News from "../../../components/globalCountries/countryhomepagescomponent/News";
import FreeDeliveryBannerSr from "../components/Freedeliverysr";
import {
  citiesAPI,
  storesAPI,
  servicesAPI,
  pricingAPI,
} from '../../../utils/api';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PricingpageSr = () => {
  const { t, i18n } = useTranslation(); // Added i18n for RTL check
  const [selectedService, setSelectedService] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('men');
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [services, setServices] = useState([]);
  const [pricingData, setPricingData] = useState({});
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingStores, setLoadingStores] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState('');

  const defaultCountryCode = 'AE';
  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL;
  const franchiseName = 'UCLEAN';

  const isRTL = i18n.language === 'ar'; // Check if the language is RTL

  // Move fetchCities outside useEffect to prevent recreation on every render
  const fetchCities = async () => {
    setLoadingCities(true);
    try {
      const response = await citiesAPI.getCities(defaultCountryCode);
      if (response.success) {
        setCities(response.data);
      } else {
        toast.error(t('pricing.errors.citiesError'));
        console.error('Error fetching cities:', response.error);
      }
    } catch (error) {
      toast.error(`${t('pricing.errors.generalError')} cities.`);
      console.error('Error fetching cities:', error);
    } finally {
      setLoadingCities(false);
    }
  };

  // Use useCallback to memoize the function
  const memoizedFetchCities = useCallback(fetchCities, [defaultCountryCode, t]);

  // Update useEffect to use memoized function
  useEffect(() => {
    memoizedFetchCities();
  }, [memoizedFetchCities]);

  useEffect(() => {
    if (selectedCity) {
      fetchStores(selectedCity.id);
      fetchServices(franchiseName, selectedCity.id, selectedStore?.id);
      if (selectedService) {
        fetchPricing(
          franchiseName,
          selectedCity.id,
          selectedService,
          selectedStore?.id
        );
      }
    } else {
      setStores([]);
      setServices([]);
      setPricingData({});
    }
  }, [selectedCity, selectedStore, selectedService]);

  const fetchStores = async (cityId) => {
    setLoadingStores(true);
    try {
      const response = await storesAPI.getStores('AE', cityId);
      if (response.success) {
        setStores(response.data.stores || []);
      } else {
        toast.error('Failed to load stores.');
        console.error('Error fetching stores:', response.error);
        setStores([]);
      }
    } catch (error) {
      toast.error('Something went wrong while loading stores.');
      console.error('Error fetching stores:', error);
      setStores([]);
    } finally {
      setLoadingStores(false);
    }
  };

  const fetchServices = async (franchise, cityId, storeId) => {
    setLoadingServices(true);
    try {
      const response = await servicesAPI.getServices(
        franchise,
        cityId,
        storeId
      );
      if (response.success) {
        setServices(response.data.services || []);
      } else {
        toast.error('Failed to load services.');
        console.error('Error fetching services:', response.error);
        setServices([]);
      }
    } catch (error) {
      toast.error('Something went wrong while loading services.');
      console.error('Error fetching services:', error);
      setServices([]);
    } finally {
      setLoadingServices(false);
    }
  };

  const fetchPricing = async (franchise, cityId, serviceId, storeId) => {
    try {
      const response = await pricingAPI.getPricing(
        franchise,
        cityId,
        serviceId,
        storeId
      );
      if (response.success) {
        setPricingData(response.data || {});
        setCurrencySymbol(response.data.currency_symbol || '');
      } else {
        toast.error('Failed to load pricing.');
        console.error('Error fetching pricing:', response.error);
        setPricingData({});
        setCurrencySymbol('');
      }
    } catch (error) {
      toast.error('Something went wrong while loading pricing.');
      console.error('Error fetching pricing:', error);
      setPricingData({});
      setCurrencySymbol('');
    } finally {
    }
  };

  const handleServiceClick = (serviceId) => {
    setSelectedService(serviceId.toString());
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const handleCitySelect = (e) => {
    const cityId = e.target.value;
    const selectedCityObj = cities.find((city) => city.id === parseInt(cityId));
    setSelectedCity(selectedCityObj);
    setSelectedStore(null);
    setPricingData({});
  };

  const handleStoreSelect = (e) => {
    const storeId = e.target.value;
    const selectedStoreObj = stores.find(
      (store) => store.id === parseInt(storeId)
    );
    setSelectedStore(selectedStoreObj);
    setPricingData({});
  };

  const getClothsForCategory = () => {
    if (pricingData.cloths && pricingData.cloths[selectedCategory]) {
      return pricingData.cloths[selectedCategory];
    }
    return null;
  };

  const clothsData = getClothsForCategory();

  const hasAddons =
    pricingData.addons &&
    selectedService &&
    pricingData.addons[selectedService] &&
    pricingData.addons[selectedService].length > 0;

  return (
    <>
      <div className='flex flex-col items-center w-full bg-white'>
        {/* Hero Banner Section */}

        <div
          className='w-full relative py-12 sm:py-16 md:py-24'
          style={{
            backgroundImage: `url(${imageBaseUrl}pricingBanner.png)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: isRTL ? 'scaleX(-1)' : 'none',
          }}
        >
          <div
            className='max-w-6xl mx-auto flex flex-col-reverse md:flex-row items-center justify-between px-4 h-full'
            dir={isRTL ? 'rtl' : 'ltr'}
            style={{
              // Reverse content back if image is flipped
              transform: isRTL ? 'scaleX(-1)' : 'none',
              width: '100%',
              // height: "100%",
            }}
          >
            {/* Image Section */}
            <div className='w-full md:w-1/2 h-auto md:h-full mb-6 md:mb-0'></div>

            {/* Text Section */}
            <div
              className={`w-full md:w-1/2 px-4 md:p-8 flex items-center justify-end md:justify-center mb-6 md:mb-0 ${
                isRTL ? 'md:justify-start' : 'md:justify-end'
              }`}
            >
              <div
                className={`${
                  isRTL ? 'text-right md:text-right' : 'text-left md:text-left'
                } text-justify`}
              >
                <h1
                  className='text-[#174C2E] uppercase'
                  style={{
                    fontFamily: 'AllRoundGothicBold',
                    fontWeight: 400,
                    fontSize: 'clamp(24px, 6vw, 42px)', // Responsive font size
                    lineHeight: '1.2',
                    letterSpacing: '1%',
                    marginBottom: '8px',
                    padding: '0 8px', // Padding for mobile
                    wordBreak: 'break-word',
                  }}
                >
                  {t('pricing.hero.title')}
                </h1>

                <h2
                  className='text-[#174C2E] uppercase'
                  style={{
                    fontFamily: 'AllRoundGothicBold',
                    fontWeight: 400,
                    fontSize: 'clamp(20px, 5vw, 36px)', // Responsive font size
                    lineHeight: '1.2',
                    letterSpacing: '1%',
                    marginBottom: '8px',
                    padding: '0 8px',
                    wordBreak: 'break-word',
                  }}
                >
                  {t('pricing.hero.subtitle')}
                </h2>

                <h2
                  className='text-[#65BA0D] uppercase'
                  style={{
                    fontFamily: 'AllRoundGothicBold',
                    fontWeight: 400,
                    fontSize: 'clamp(20px, 5vw, 36px)', // Responsive font size
                    lineHeight: '1.2',
                    letterSpacing: '1%',
                    padding: '0 8px',
                    wordBreak: 'break-word',
                  }}
                >
                  {t('pricing.hero.subSubtitle')}
                </h2>
              </div>
            </div>
          </div>
        </div>

        {/* Intro text */}
        <div className='w-full max-w-6xl py-8 text-center text-sm px-4 mt-8'>
          <p
            className='text-[#666666] font-[400] leading-[100%] tracking-[1%] font-poppins'
            style={{
              fontSize: '14px',
            }}
          >
            {t('pricing.hero.description')}
          </p>
          <style>
            {`
              @media (min-width: 768px) {
                .font-poppins {
                  font-size: 18px !important;
                }
              }
            `}
          </style>
        </div>

        {/* Main Content Section */}
        <div className='w-full max-w-6xl px-4 pt-8'>
          {/* Location Selectors */}
          {/* Location Selectors */}
          <div
            className={`grid grid-cols-1 md:grid-cols-2 gap-4 mb-8`} // Removed isRTL flex modifiers
          >
            <select
              value={selectedCity ? selectedCity.id : ''}
              onChange={handleCitySelect}
              className={`w-full p-3 border border-gray-300 rounded-lg`}
              style={{ direction: 'ltr', textAlign: 'left' }}
            >
              <option value=''>{t('pricing.location.cityPlaceholder')}</option>
              {loadingCities ? (
                <option disabled>{t('pricing.location.loading')}</option>
              ) : (
                cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))
              )}
            </select>

            <select
              value={selectedStore ? selectedStore.id : ''}
              onChange={handleStoreSelect}
              className={`w-full p-3 border border-gray-300 rounded-lg`}
              style={{ direction: 'ltr', textAlign: 'left' }}
              disabled={!selectedCity}
            >
              <option value=''>{t('pricing.location.storePlaceholder')}</option>
              {loadingStores ? (
                <option disabled>{t('pricing.location.loading')}</option>
              ) : (
                Array.isArray(stores) &&
                stores.map((store) => (
                  <option key={store.id} value={store.id}>
                    {store.name}
                  </option>
                ))
              )}
            </select>
          </div>
          {/* Service Selection Section */}
          <div className='w-full max-w-6xl mb-8 px-4'>
            {/* Title moved to top */}
            <div className={`text-${isRTL ? 'right' : 'left'} mb-4`}>
              <h3
                className='text-lg font-bold'
                style={{ color: globalStyles.colors.army_green }}
              >
                {t('pricing.services.title')}
              </h3>
            </div>

            <div
              className={`flex flex-col ${
                isRTL ? 'md:flex-row-reverse' : 'md:flex-row'
              }`}
            >
              {/* Services Column - Will be on left for English, right for Arabic */}
              <div
                className={`w-full md:w-1/3 ${
                  isRTL ? 'pl-0 md:pl-4' : 'pr-0 md:pr-4'
                }`}
              >
                {/* Service buttons */}
                {loadingServices ? (
                  <div>{t('pricing.services.loading')}</div>
                ) : (
                  <div className='flex flex-col gap-1'>
                    {Array.isArray(services) &&
                      services.map((service) => {
                        return (
                          <button
                            key={service.id}
                            className={`border rounded p-4 flex items-center  ${
                              // Removed justify-start/end for controlled alignment
                              selectedService === service.id.toString()
                                ? 'text-white'
                                : 'text-gray-800'
                            }`}
                            style={{
                              backgroundColor:
                                selectedService === service.id.toString()
                                  ? globalStyles.colors.army_green
                                  : 'transparent',
                              borderColor:
                                selectedService === service.id.toString()
                                  ? globalStyles.colors.army_green
                                  : '#BBBBBB',
                              justifyContent: 'space-between', // ADDED: Push items to edges
                              flexDirection: isRTL ? 'row-reverse' : 'row', //Ensure text and icon swap in RTL
                            }}
                            onClick={() => handleServiceClick(service.id)}
                          >
                            <span
                              style={{ textAlign: isRTL ? 'right' : 'left' }}
                            >
                              {service.name}
                            </span>
                            <div className='p-1 rounded'>
                              <img
                                src={service.full_url} // Using full_url directly
                                alt={`icon`}
                                className={`h-12 w-12`}
                              />
                            </div>
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Categories and Pricing Table Column */}
              <div className='w-full md:w-2/3 mt-4 md:mt-0'>
                {/* Categories */}
                <div
                  //CHANGED: Removed justify-start justify-end
                  className={`mb-4 flex items-center`}
                >
                  <div
                    className={`flex gap-3 flex-wrap`}
                    style={{
                      flexDirection: isRTL ? 'row-reverse' : 'row', //CHANGED: fixed directino for ar
                    }}
                  >
                    {[
                      {
                        key: 'men',
                        icon: (
                          <svg
                            className='h-5 w-5 mr-2'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            viewBox='0 0 24 24'
                          >
                            <circle cx='12' cy='8' r='4' />
                            <path d='M6 20v-2a4 4 0 018 0v2' />
                          </svg>
                        ),
                      },
                      {
                        key: 'women',
                        icon: (
                          <svg
                            className='h-5 w-5 mr-2'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            viewBox='0 0 24 24'
                          >
                            <circle cx='12' cy='8' r='4' />
                            <path d='M12 12v6M9 18h6' />
                          </svg>
                        ),
                      },
                      {
                        key: 'household',
                        icon: (
                          <svg
                            className='h-5 w-5 mr-2'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            viewBox='0 0 24 24'
                          >
                            <path d='M3 12l9-9 9 9' />
                            <path d='M9 21V9h6v12' />
                          </svg>
                        ),
                      },
                      {
                        key: 'kids',
                        icon: (
                          <svg
                            className='h-5 w-5 mr-2'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            viewBox='0 0 24 24'
                          >
                            <circle cx='12' cy='8' r='4' />
                            <path d='M5 20c0-3 4-5 7-5s7 2 7 5' />
                          </svg>
                        ),
                      },
                      {
                        key: 'luxury',
                        icon: (
                          <svg
                            className='h-5 w-5 mr-2'
                            fill='none'
                            stroke='currentColor'
                            strokeWidth={2}
                            viewBox='0 0 24 24'
                          >
                            <circle cx='12' cy='8' r='4' />
                            <path d='M2 20h20M7 20v-2a5 5 0 0110 0v2' />
                          </svg>
                        ),
                      },
                    ].map((category) => (
                      <button
                        key={category.key}
                        onClick={() => handleCategoryClick(category.key)}
                        className={`flex items-center px-4 py-2 rounded-lg border text-sm md:text-base transition-colors duration-150 ${
                          selectedCategory === category.key
                            ? 'text-white shadow'
                            : 'text-gray-800 bg-white'
                        }`}
                        style={{
                          backgroundColor:
                            selectedCategory === category.key
                              ? globalStyles.colors.army_green
                              : 'transparent',
                          borderColor:
                            selectedCategory === category.key
                              ? globalStyles.colors.army_green
                              : '#BBBBBB',
                          opacity: selectedCategory === category.key ? 1 : 0.85,
                          flexDirection: isRTL ? 'row-reverse' : 'row', //CHANGED: for categories
                        }}
                      >
                        <span
                          className='flex items-center'
                          style={{
                            direction: isRTL ? 'rtl' : 'ltr',
                            display: 'flex',
                            flexDirection: isRTL ? 'row-reverse' : 'row',
                          }}
                        >
                          {category.icon}
                          {t(`pricing.services.categories.${category.key}`)}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Cloths Price list header */}
                <div
                  className='border rounded mb-4'
                  style={{ borderColor: '#BBBBBB' }}
                >
                  {/* Header */}
                  <div
                    className={`flex ${
                      isRTL ? 'flex-row-reverse' : 'flex-row'
                    }`}
                    style={{
                      backgroundColor: globalStyles.colors.army_green,
                      color: 'white',
                    }}
                  >
                    <div
                      className={`w-1/2 p-2 ${
                        isRTL ? 'text-right' : 'text-left' //CHANGED:  for table
                      } font-bold`}
                      style={{ textAlign: isRTL ? 'right' : 'left' }}
                    >
                      {t('pricing.services.priceList.itemName')}
                    </div>
                    <div
                      className={`w-1/2 p-2 ${
                        isRTL ? 'text-left' : 'text-right' //CHANGED:  for table
                      } font-bold`}
                      style={{ textAlign: isRTL ? 'left' : 'right' }}
                    >
                      {t('pricing.services.priceList.price')}
                    </div>
                  </div>

                  {/* Services Row */}
                  {clothsData && clothsData.length > 0 ? (
                    clothsData.map((cloth) => (
                      <div
                        key={cloth.id}
                        className={`flex ${
                          isRTL ? 'flex-row-reverse' : 'flex-row'
                        } border-b`}
                        style={{ borderColor: '#BBBBBB' }}
                      >
                        <div
                          className={`w-1/2 p-2 ${
                            isRTL ? 'text-right' : 'text-left' //CHANGED:  for table
                          }`}
                          style={{ textAlign: isRTL ? 'right' : 'left' }}
                        >
                          {cloth.name}
                        </div>
                        <div
                          className={`w-1/2 p-2 ${
                            isRTL ? 'text-left' : 'text-right' //CHANGED:  for table
                          }`}
                          style={{ textAlign: isRTL ? 'left' : 'right' }}
                        >
                          {currencySymbol} {cloth.price}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div
                      className={`flex ${
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      } border-b`}
                      style={{ borderColor: '#BBBBBB' }}
                    >
                      <div
                        className={`w-1/2 p-2 ${
                          isRTL ? 'text-right' : 'text-left' //CHANGED:  for table
                        }`}
                        style={{ textAlign: isRTL ? 'right' : 'left' }}
                      >
                        No data available
                      </div>
                      <div
                        className={`w-1/2 p-2 ${
                          isRTL ? 'text-left' : 'text-right' //CHANGED:  for table
                        }`}
                        style={{ textAlign: isRTL ? 'left' : 'right' }}
                      >
                        {/* No Data */}
                      </div>
                    </div>
                  )}
                </div>

                {/* Addons Section */}
                {hasAddons && (
                  <div
                    className='border rounded'
                    style={{ borderColor: '#BBBBBB' }}
                  >
                    {/* Header */}
                    <div
                      className={`flex ${
                        isRTL ? 'flex-row-reverse' : 'flex-row'
                      }`}
                      style={{
                        backgroundColor: globalStyles.colors.army_green,
                        color: 'white',
                      }}
                    >
                      <div
                        className={`w-1/2 p-2 ${
                          isRTL ? 'text-right' : 'text-left' //CHANGED:  for table
                        } font-bold`}
                        style={{ textAlign: isRTL ? 'right' : 'left' }}
                      >
                        Addon Name
                      </div>
                      <div
                        className={`w-1/2 p-2 ${
                          isRTL ? 'text-left' : 'text-right' //CHANGED:  for table
                        } font-bold`}
                        style={{ textAlign: isRTL ? 'left' : 'right' }}
                      >
                        {t('pricing.services.priceList.price')}
                      </div>
                    </div>

                    {/* Addons Row */}
                    {pricingData.addons[selectedService].map((addon) => (
                      <div
                        key={addon.id}
                        className={`flex ${
                          isRTL ? 'flex-row-reverse' : 'flex-row'
                        } border-b`}
                        style={{ borderColor: '#BBBBBB' }}
                      >
                        <div
                          className={`w-1/2 p-2 ${
                            isRTL ? 'text-right' : 'text-left' //CHANGED:  for table
                          }`}
                          style={{ textAlign: isRTL ? 'right' : 'left' }}
                        >
                          {addon.name}
                        </div>
                        <div
                          className={`w-1/2 p-2 ${
                            isRTL ? 'text-left' : 'text-right' //CHANGED:  for table
                          }`}
                          style={{ textAlign: isRTL ? 'left' : 'right' }}
                        >
                          {currencySymbol} {addon.price}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <FreeDeliveryBannerSr />
        </div>
        <News />
      </div>
      <FooterSr />
    </>
  );
};

export default PricingpageSr;
